import { useState, useRef, useEffect, useMemo, Fragment } from 'react';
import useSquarespaceOrders from '../../hooks/useSquarespaceOrders';
import Loading from '../../components/common/Loading';
import Alert from '../../components/common/Alert';
import { formatCurrency, formatDate } from '../../utils/formatters';
import './EmployeeListPage.css';
import './Orders.css';
import './OnlineOrders.css';
import '../../components/orders/OrderLineItemsTable.css';
import '../../components/orders/ProductSummaryTable.css';

const FULFILLMENT_OPTIONS = [
  { value: '',          label: 'All' },
  { value: 'pending',   label: 'Pending' },
  { value: 'fulfilled', label: 'Fulfilled' },
];

const FulfillmentBadge = ({ status }) => {
  const cls = `online-orders__badge online-orders__badge--${status || 'pending'}`;
  const label =
    status === 'fulfilled' ? 'Fulfilled'
    : status === 'partial'  ? 'Partial'
    : 'Pending';
  return <span className={cls}>{label}</span>;
};

const DeliveryBadge = ({ shippingMethod }) => {
  const isPickup = shippingMethod === 'Local Pickup';
  const cls = `online-orders__badge online-orders__badge--${isPickup ? 'pickup' : 'ship'}`;
  return <span className={cls}>{isPickup ? 'Pickup' : 'Ship'}</span>;
};

const TABS = [
  { key: 'orders',     label: 'Orders' },
  { key: 'line-items', label: 'Line Items' },
  { key: 'summary',    label: 'Summary' },
];

// ── Line Items tab ───────────────────────────────────────────────────────────
const SqLineItemsTable = ({ orders, loading }) => {
  const rows = useMemo(() =>
    orders.flatMap((order) =>
      (order.line_items || []).map((item) => ({
        orderId:     order.id,
        orderNumber: order.order_number,
        customer:    order.customer_name || order.customer_email || '—',
        date:        order.created_at,
        method:      order.shipping_method || '',
        productName: item.name || '—',
        variantName: item.variant || '',
        quantity:    item.quantity,
        unitPrice:   item.unit_price,
        subtotal:    item.line_total,
      }))
    ),
    [orders]
  );

  const handleExport = () => {
    const headers = ['Order #', 'Date', 'Customer', 'Method', 'Product', 'Variant', 'Qty', 'Unit Price', 'Subtotal'];
    const esc = (v) => { const s = String(v ?? ''); return s.includes(',') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s; };
    const lines = [headers.join(','), ...rows.map((r) => [
      esc(r.orderNumber), esc(r.date ? formatDate(r.date) : ''), esc(r.customer),
      esc(r.method), esc(r.productName), esc(r.variantName),
      esc(r.quantity), esc(r.unitPrice), esc(r.subtotal),
    ].join(','))];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `sq-line-items-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 100);
  };

  if (loading) return <div className="oli-table__empty"><p className="oli-table__empty-text">Loading…</p></div>;
  if (rows.length === 0) return (
    <div className="oli-table__empty">
      <p className="oli-table__empty-label">No Line Items</p>
      <p className="oli-table__empty-sub">No orders with items match the current filters.</p>
    </div>
  );

  return (
    <div className="oli-table-wrap">
      <div className="oli-table__export-bar">
        <button type="button" className="oli-table__export-btn" onClick={handleExport}>Export CSV</button>
      </div>
      <table className="oli-table">
        <thead className="oli-table__thead">
          <tr>
            <th className="oli-table__th">Customer</th>
            <th className="oli-table__th">Product</th>
            <th className="oli-table__th oli-table__col--hide-sm oli-table__th--size">Variant</th>
            <th className="oli-table__th oli-table__th--num oli-table__th--qty">Qty</th>
            <th className="oli-table__th oli-table__th--num oli-table__th--total">Subtotal</th>
            <th className="oli-table__th oli-table__col--hide-sm oli-table__th--status">Method</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={`${row.orderId}-${idx}`} className="oli-table__row" style={{ animationDelay: `${idx * 0.02}s` }}>
              <td className="oli-table__td oli-table__td--company">
                <span className="oli-table__company-name">{row.customer}</span>
                {row.date && <span className="oli-table__placed-date">#{row.orderNumber} · {formatDate(row.date)}</span>}
              </td>
              <td className="oli-table__td oli-table__td--product">
                <span className="oli-table__product-name">{row.productName}</span>
                <span className="oli-table__product-meta">
                  <span className="oli-table__variant-inline">{row.variantName}</span>
                </span>
              </td>
              <td className="oli-table__td oli-table__td--variant oli-table__col--hide-sm oli-table__td--size">{row.variantName}</td>
              <td className="oli-table__td oli-table__td--num">{row.quantity}</td>
              <td className="oli-table__td oli-table__td--num oli-table__td--bold oli-table__td--total">
                {row.subtotal != null ? formatCurrency(parseFloat(row.subtotal)) : '—'}
              </td>
              <td className="oli-table__td oli-table__col--hide-sm oli-table__td--status">
                {row.method && <DeliveryBadge shippingMethod={row.method} />}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── Summary tab ──────────────────────────────────────────────────────────────
const SqSummaryTable = ({ orders, loading }) => {
  const [expandedProducts, setExpandedProducts] = useState(new Set());

  const { products, grandTotal, grandValue } = useMemo(() => {
    const map = {};
    orders.forEach((order) => {
      (order.line_items || []).forEach((item) => {
        const pName = item.name || 'Unknown';
        const vName = item.variant || 'Unknown';
        const qty   = Number(item.quantity) || 0;
        const value = parseFloat(item.line_total) || 0;
        if (!map[pName]) map[pName] = { totalQty: 0, totalValue: 0, variants: {} };
        map[pName].totalQty   += qty;
        map[pName].totalValue += value;
        if (!map[pName].variants[vName]) map[pName].variants[vName] = { qty: 0, value: 0 };
        map[pName].variants[vName].qty   += qty;
        map[pName].variants[vName].value += value;
      });
    });
    const products = Object.entries(map)
      .map(([name, d]) => ({
        name,
        totalQty:   d.totalQty,
        totalValue: d.totalValue,
        variants: Object.entries(d.variants)
          .map(([vName, vd]) => ({ name: vName, qty: vd.qty, value: vd.value }))
          .sort((a, b) => b.qty - a.qty),
      }))
      .sort((a, b) => b.totalQty - a.totalQty);
    const grandTotal = products.reduce((s, p) => s + p.totalQty, 0);
    const grandValue = products.reduce((s, p) => s + p.totalValue, 0);
    return { products, grandTotal, grandValue };
  }, [orders]);

  const toggle = (name) => setExpandedProducts((prev) => {
    const next = new Set(prev);
    next.has(name) ? next.delete(name) : next.add(name);
    return next;
  });

  const fmtVal = (v) => v > 0 ? formatCurrency(v) : '—';

  if (loading) return <div className="pst__empty"><p className="pst__empty-text">Loading…</p></div>;
  if (products.length === 0) return (
    <div className="pst__empty">
      <p className="pst__empty-label">No Data</p>
      <p className="pst__empty-sub">No order items match the current filters.</p>
    </div>
  );

  return (
    <div className="pst-wrap">
      <div className="pst-stats">
        <div className="pst-stat">
          <span className="pst-stat__value">{products.length}</span>
          <span className="pst-stat__label">{products.length === 1 ? 'Product' : 'Products'}</span>
        </div>
        <div className="pst-stat pst-stat--divider">
          <span className="pst-stat__value">{grandTotal.toLocaleString()}</span>
          <span className="pst-stat__label">Total Units</span>
        </div>
        <div className="pst-stat pst-stat--divider pst-stat--highlight">
          <span className="pst-stat__value">{fmtVal(grandValue)}</span>
          <span className="pst-stat__label">Total Value</span>
        </div>
      </div>
      <div className="pst-scroll">
        <table className="pst">
          <thead className="pst__thead">
            <tr>
              <th className="pst__th pst__th--product">Product</th>
              <th className="pst__th pst__th--num">Units</th>
              <th className="pst__th pst__th--num">Value</th>
              <th className="pst__th pst__th--pct pst__th--share">Share</th>
            </tr>
          </thead>
          <tbody>
            {products.map((product) => {
              const isExpanded = expandedProducts.has(product.name);
              const pct = grandTotal > 0 ? ((product.totalQty / grandTotal) * 100).toFixed(1) : '0.0';
              return (
                <Fragment key={product.name}>
                  <tr
                    className={`pst__row pst__row--product${isExpanded ? ' pst__row--expanded' : ''}`}
                    onClick={() => toggle(product.name)}
                    role="button" tabIndex={0}
                    aria-expanded={isExpanded}
                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggle(product.name)}
                  >
                    <td className="pst__td pst__td--product">
                      <div className="pst__cell-inner">
                        <span className={`pst__chevron${isExpanded ? ' pst__chevron--open' : ''}`}>›</span>
                        <span className="pst__product-name">{product.name}</span>
                      </div>
                    </td>
                    <td className="pst__td pst__td--num pst__td--bold">{product.totalQty.toLocaleString()}</td>
                    <td className="pst__td pst__td--num">{fmtVal(product.totalValue)}</td>
                    <td className="pst__td pst__td--pct pst__td--share">
                      <span className="pst__bar-wrap"><span className="pst__bar" style={{ width: `${pct}%` }} /></span>
                      <span className="pst__pct-label">{pct}%</span>
                    </td>
                  </tr>
                  {isExpanded && product.variants.map((variant) => {
                    const vPct = grandTotal > 0 ? ((variant.qty / grandTotal) * 100).toFixed(1) : '0.0';
                    return (
                      <tr key={`${product.name}__${variant.name}`} className="pst__row pst__row--variant">
                        <td className="pst__td pst__td--variant">{variant.name}</td>
                        <td className="pst__td pst__td--num">{variant.qty.toLocaleString()}</td>
                        <td className="pst__td pst__td--num">{fmtVal(variant.value)}</td>
                        <td className="pst__td pst__td--pct pst__td--share">
                          <span className="pst__bar-wrap"><span className="pst__bar pst__bar--variant" style={{ width: `${vPct}%` }} /></span>
                          <span className="pst__pct-label">{vPct}%</span>
                        </td>
                      </tr>
                    );
                  })}
                </Fragment>
              );
            })}
          </tbody>
          <tfoot className="pst__tfoot">
            <tr>
              <td className="pst__td pst__td--total-label">Total</td>
              <td className="pst__td pst__td--num pst__td--bold">{grandTotal.toLocaleString()}</td>
              <td className="pst__td pst__td--num pst__td--bold">{fmtVal(grandValue)}</td>
              <td className="pst__td pst__td--share" />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

const OnlineOrders = () => {
  // ── Active tab ────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('orders');

  // ── Filters ──────────────────────────────────────────────
  const [fulfillmentFilter, setFulfillmentFilter] = useState('');
  const [searchTerm, setSearchTerm]               = useState('');
  const [dateFrom, setDateFrom]                   = useState('');
  const [dateTo, setDateTo]                       = useState('');
  const debounceRef = useRef(null);

  // ── Alert ────────────────────────────────────────────────
  const [alert, setAlert] = useState(null);

  // ── Expanded rows (show line items) ─────────────────────
  const [expandedIds, setExpandedIds] = useState(new Set());

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // ── Data ─────────────────────────────────────────────────
  const {
    orders: allOrders,
    loading,
    isRefreshing,
    error,
    hasMore,
    fetchOrders,
    fetchNextPage,
    clearError,
  } = useSquarespaceOrders();

  // ── Current filter state ref (for load-more) ─────────────
  const filterRef = useRef({ fulfillmentFilter, dateFrom, dateTo });
  useEffect(() => {
    filterRef.current = { fulfillmentFilter, dateFrom, dateTo };
  }, [fulfillmentFilter, dateFrom, dateTo]);

  // ── Sync errors ───────────────────────────────────────────
  useEffect(() => {
    if (error) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setAlert({ type: 'error', message: error.message || 'Failed to load online orders.' });
      clearError();
    }
  }, [error, clearError]);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  // ── Refetch with current filters ─────────────────────────
  const doFetch = (overrides = {}) => {
    const f = { ...filterRef.current, ...overrides };
    fetchOrders({
      fulfillmentStatus: f.fulfillmentFilter,
      dateFrom:          f.dateFrom,
      dateTo:            f.dateTo,
    });
  };

  const handleFulfillmentFilter = (val) => {
    setFulfillmentFilter(val);
    doFetch({ fulfillmentFilter: val });
  };

  const handleDateFrom = (e) => {
    const val = e.target.value;
    setDateFrom(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doFetch({ dateFrom: val }), 300);
  };

  const handleDateTo = (e) => {
    const val = e.target.value;
    setDateTo(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doFetch({ dateTo: val }), 300);
  };

  const handleClearDates = () => {
    setDateFrom('');
    setDateTo('');
    doFetch({ dateFrom: '', dateTo: '' });
  };

  const handleLoadMore = () => {
    fetchNextPage({
      fulfillmentStatus: fulfillmentFilter,
      dateFrom,
      dateTo,
    });
  };

  // ── Client-side search (by name, email, order number) ────
  const filteredOrders = allOrders.filter((order) => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (
      order.order_number?.toLowerCase().includes(q) ||
      order.customer_name?.toLowerCase().includes(q) ||
      order.customer_email?.toLowerCase().includes(q)
    );
  });

  const hasDateFilter = dateFrom || dateTo;
  const orderCount    = filteredOrders.length;

  const lineItemCount = useMemo(
    () => filteredOrders.reduce((sum, o) => sum + (o.line_items?.length || 0), 0),
    [filteredOrders]
  );
  const productCount = useMemo(() => {
    const names = new Set();
    filteredOrders.forEach((o) => (o.line_items || []).forEach((i) => {
      if (i.name) names.add(i.name);
    }));
    return names.size;
  }, [filteredOrders]);

  const countLabel = activeTab === 'line-items' ? lineItemCount
    : activeTab === 'summary' ? productCount
    : orderCount;
  const countNoun = activeTab === 'line-items'
    ? (lineItemCount === 1 ? 'line item'  : 'line items')
    : activeTab === 'summary'
    ? (productCount  === 1 ? 'product'    : 'products')
    : (orderCount    === 1 ? 'order'      : 'orders');

  return (
    <div className="employee-page online-orders-page">

      {/* ── Alert ─────────────────────────────────────────── */}
      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          dismissible
          onDismiss={() => setAlert(null)}
          className="emp-list__alert"
        />
      )}

      {/* ── Header ────────────────────────────────────────── */}
      <div className="employee-page__header">
        <p className="employee-page__eyebrow">Employee Portal</p>
        <h1 className="employee-page__title">Online Orders</h1>
        <p className="employee-page__subtitle">Squarespace website orders</p>
      </div>

      {/* ── Tabs ──────────────────────────────────────────── */}
      <div className="orders-tabs" role="tablist" aria-label="Online orders view">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            role="tab"
            type="button"
            aria-selected={activeTab === tab.key}
            className={`orders-tab${activeTab === tab.key ? ' orders-tab--active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Toolbar ───────────────────────────────────────── */}
      <div className="emp-list__toolbar online-orders-toolbar">
        <div className="emp-list__toolbar-left">

          {/* Search */}
          <div className="emp-list__search-wrap">
            <svg className="emp-list__search-icon" viewBox="0 0 20 20" aria-hidden="true">
              <path
                d="M8.5 3a5.5 5.5 0 014.383 8.823l3.896 3.9a.75.75 0 01-1.06 1.06l-3.9-3.896A5.5 5.5 0 118.5 3zm0 1.5a4 4 0 100 8 4 4 0 000-8z"
                fill="currentColor"
              />
            </svg>
            <input
              type="text"
              className="emp-list__search"
              placeholder="Search by order #, name, or email…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search online orders"
            />
          </div>

          {/* Date range */}
          <div className="orders-date-range">
            <div className="orders-date-field">
              <label className="orders-date-label" htmlFor="online-date-from">From</label>
              <input
                id="online-date-from"
                type="date"
                className="orders-date-input"
                value={dateFrom}
                max={dateTo || undefined}
                onChange={handleDateFrom}
              />
            </div>
            <span className="orders-date-sep" aria-hidden="true">–</span>
            <div className="orders-date-field">
              <label className="orders-date-label" htmlFor="online-date-to">To</label>
              <input
                id="online-date-to"
                type="date"
                className="orders-date-input"
                value={dateTo}
                min={dateFrom || undefined}
                onChange={handleDateTo}
              />
            </div>
            {hasDateFilter && (
              <button
                type="button"
                className="orders-date-clear"
                onClick={handleClearDates}
                aria-label="Clear date filter"
              >
                ×
              </button>
            )}
          </div>
        </div>

        {countLabel > 0 && (
          <span className="orders-count__label">
            {countLabel} {countNoun}
          </span>
        )}
      </div>

      {/* ── Fulfillment filter pills ───────────────────────── */}
      <div className="orders-filters-wrap">
        <div className="emp-list__filters orders-status-filters">
          {FULFILLMENT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`emp-list__filter${fulfillmentFilter === opt.value ? ' emp-list__filter--active' : ''}`}
              onClick={() => handleFulfillmentFilter(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Content ───────────────────────────────────────── */}
      <div className="emp-list__body orders-list-body" role="tabpanel">

        {activeTab === 'line-items' ? (
          <SqLineItemsTable orders={filteredOrders} loading={loading && allOrders.length === 0} />
        ) : activeTab === 'summary' ? (
          <SqSummaryTable orders={filteredOrders} loading={loading && allOrders.length === 0} />
        ) : loading && allOrders.length === 0 ? (
          <div className="order-list__state">
            <Loading />
            <p className="order-list__state-text">Loading online orders…</p>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="order-list__empty">
            <p className="order-list__empty-label">No Online Orders</p>
            <p className="order-list__empty-sub">
              No Squarespace orders match your current filters.
            </p>
          </div>
        ) : (
          <>
            {/* ── Table header ── */}
            <div className="online-orders__thead">
              <span className="online-orders__col-label">Order</span>
              <span className="online-orders__col-label online-orders__col-label--hide-sm">Date</span>
              <span className="online-orders__col-label online-orders__col-label--hide-sm">Customer</span>
              <span className="online-orders__col-label online-orders__col-label--hide-sm">Items</span>
              <span className="online-orders__col-label online-orders__col-label--right online-orders__col-label--hide-sm">Total</span>
              <span className="online-orders__col-label online-orders__col-label--hide-sm">Status</span>
              <span />
            </div>

            {/* ── Rows ── */}
            {filteredOrders.map((order, index) => {
              const isExpanded = expandedIds.has(order.id);
              return (
                <div
                  key={order.id}
                  className="online-orders__row-wrapper"
                  style={{ animationDelay: `${index * 0.04}s` }}
                >
                  {/* Summary row */}
                  <div
                    className={`online-orders__row${isExpanded ? ' online-orders__row--expanded' : ''}`}
                    onClick={() => toggleExpand(order.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && toggleExpand(order.id)}
                    aria-expanded={isExpanded}
                  >
                    {/* Order number */}
                    <div className="online-orders__row-primary">
                      <div className="online-orders__order-number-row">
                        <span className="online-orders__order-number">#{order.order_number}</span>
                        <DeliveryBadge shippingMethod={order.shipping_method} />
                      </div>
                      <span className="online-orders__channel">{order.channel}</span>
                      {/* Mobile meta */}
                      <div className="online-orders__row-meta">
                        <span className="online-orders__total--meta">
                          {order.total ? formatCurrency(parseFloat(order.total)) : '—'}
                        </span>
                        <FulfillmentBadge status={order.fulfillment_status} />
                      </div>
                    </div>

                    {/* Date */}
                    <div className="online-orders__col online-orders__col--hide-sm">
                      {order.created_at ? formatDate(order.created_at) : '—'}
                    </div>

                    {/* Customer */}
                    <div className="online-orders__col online-orders__col--hide-sm">
                      <span className="online-orders__customer-name">{order.customer_name || '—'}</span>
                      {order.customer_email && (
                        <span className="online-orders__customer-email">{order.customer_email}</span>
                      )}
                    </div>

                    {/* Item count */}
                    <div className="online-orders__col online-orders__col--hide-sm">
                      {order.item_count} {order.item_count === 1 ? 'item' : 'items'}
                    </div>

                    {/* Total */}
                    <div className="online-orders__col online-orders__col--right online-orders__col--hide-sm">
                      {order.total ? formatCurrency(parseFloat(order.total)) : '—'}
                    </div>

                    {/* Status */}
                    <div className="online-orders__col online-orders__col--hide-sm">
                      <FulfillmentBadge status={order.fulfillment_status} />
                    </div>

                    {/* Expand toggle */}
                    <div className="online-orders__expand-icon" aria-hidden="true">
                      {isExpanded ? '▲' : '▼'}
                    </div>
                  </div>

                  {/* Line items (expanded) */}
                  {isExpanded && (
                    <div className="online-orders__line-items">
                      <table className="online-orders__items-table">
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th>Variant</th>
                            <th>SKU</th>
                            <th className="online-orders__items-table-right">Qty</th>
                            <th className="online-orders__items-table-right">Unit Price</th>
                            <th className="online-orders__items-table-right">Line Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {order.line_items.map((item) => (
                            <tr key={item.id}>
                              <td>{item.name}</td>
                              <td>{item.variant || '—'}</td>
                              <td className="online-orders__sku">{item.sku || '—'}</td>
                              <td className="online-orders__items-table-right">{item.quantity}</td>
                              <td className="online-orders__items-table-right">
                                {formatCurrency(parseFloat(item.unit_price))}
                              </td>
                              <td className="online-orders__items-table-right">
                                {formatCurrency(parseFloat(item.line_total))}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td colSpan={5} className="online-orders__items-table-right online-orders__order-total-label">
                              Order Total
                            </td>
                            <td className="online-orders__items-table-right online-orders__order-total-value">
                              {order.total ? formatCurrency(parseFloat(order.total)) : '—'}
                            </td>
                          </tr>
                        </tfoot>
                      </table>

                      {/* Shipping method + address */}
                      {order.shipping_method && (
                        <div className="online-orders__shipping">
                          <span className="online-orders__shipping-label">Method:</span>
                          <span className="online-orders__shipping-value">{order.shipping_method}</span>
                        </div>
                      )}
                      {order.shipping_address?.address ? (
                        <div className="online-orders__shipping">
                          <span className="online-orders__shipping-label">Ship to:</span>
                          <span className="online-orders__shipping-value">
                            {order.shipping_address.name && `${order.shipping_address.name} · `}
                            {order.shipping_address.address}, {order.shipping_address.city},{' '}
                            {order.shipping_address.state} {order.shipping_address.zip}
                          </span>
                        </div>
                      ) : order.billing_address?.city && (
                        <div className="online-orders__shipping">
                          <span className="online-orders__shipping-label">Customer location:</span>
                          <span className="online-orders__shipping-value">
                            {order.billing_address.city}, {order.billing_address.state}
                          </span>
                        </div>
                      )}

                      {/* Fulfilled date */}
                      {order.fulfilled_at && (
                        <div className="online-orders__shipping">
                          <span className="online-orders__shipping-label">Fulfilled:</span>
                          <span className="online-orders__shipping-value">
                            {formatDate(order.fulfilled_at)}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {/* ── Load more ── */}
            {hasMore && (
              <div className="online-orders__load-more">
                <button
                  type="button"
                  className="online-orders__load-more-btn"
                  onClick={handleLoadMore}
                  disabled={isRefreshing}
                >
                  {isRefreshing ? 'Loading…' : 'Load more orders'}
                </button>
              </div>
            )}
          </>
        )}

        {activeTab === 'orders' && isRefreshing && allOrders.length > 0 && (
          <div className="emp-list__refreshing" />
        )}
      </div>
    </div>
  );
};

export default OnlineOrders;
